import { CommonModule } from '@angular/common';
import {
  Component,
  ElementRef,
  input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import * as Highcharts from 'highcharts';
import {
  IScoreWeight,
  SectionType,
  SECTION_ORDER,
} from 'src/app/interfaces/self-serve/isimulation-score';
import { LocaleService } from 'src/app/services/locale.service';

const SECTION_COLORS: Record<SectionType, string> = {
  Messaging: '#A2CED4',
  Methodology: '#BDB4FE',
  Skills: '#EDB89A',
};

@Component({
  selector: 'score-distribution-chart',
  standalone: true,
  templateUrl: './score-distribution-chart.component.html',
  styleUrls: ['./score-distribution-chart.component.scss'],
  imports: [CommonModule],
})
export class ScoreDistributionChartComponent implements OnChanges, OnDestroy {
  @ViewChild('chartContainer', { static: true })
  chartContainer!: ElementRef<HTMLDivElement>;
  weights = input<IScoreWeight[]>([]);

  chartOptions: Highcharts.Options = {};
  private chart?: Highcharts.Chart;

  constructor(private localeService: LocaleService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['weights']) {
      this.initChart();
      this.drawChart();
    }
  }

  ngOnDestroy(): void {
    this.chart?.destroy();
  }

  private initChart(): void {
    const localeService = this.localeService;
    const enabledWeights = (this.weights() || [])
      .filter((weight) => !weight.disabled && weight.value > 0)
      .sort(
        (a, b) =>
          SECTION_ORDER.indexOf(a.section) - SECTION_ORDER.indexOf(b.section)
      );

    const series: Highcharts.SeriesBarOptions[] = enabledWeights.map(
      (weight) => ({
        type: 'bar',
        name: weight.section,
        data: [Number(weight.value)],
        color: SECTION_COLORS[weight.section],
      })
    );

    this.chartOptions = {
      chart: {
        type: 'bar',
        height: 97,
        backgroundColor: 'transparent',
      },
      title: { text: undefined },
      xAxis: { categories: [''], visible: false },
      yAxis: {
        min: 0,
        max: 100,
        visible: false,
        title: { text: undefined },
        reversedStacks: false,
      },
      legend: {
        enabled: true,
        align: 'left',
        verticalAlign: 'bottom',
        symbolRadius: 0,
        symbolHeight: 16,
        symbolWidth: 16,
        x: -8,
        itemStyle: {
          fontWeight: '400',
          fontSize: '16px',
          color: '#000000',
          cursor: 'default',
        },
        itemHoverStyle: { color: '#000000' },
        events: {
          itemClick: function () {
            return false;
          },
        },
      },
      tooltip: { enabled: false },
      credits: { enabled: false },
      plotOptions: {
        series: {
          stacking: 'normal',
          borderWidth: 0,
          states: {
            hover: { enabled: false },
            inactive: { enabled: false },
          },
        },
        bar: {
          pointWidth: 30,
          pointPadding: 0,
          groupPadding: 0,
          dataLabels: {
            enabled: true,
            inside: true,
            verticalAlign: 'middle',
            align: 'center',
            formatter: function (): string {
              const value = Number(this.y);
              return localeService.formatPercentage(
                value,
                localeService.getDecimalPlaces(value, 2)
              );
            },
            style: {
              fontWeight: '600',
              fontSize: '20px',
              color: '#000000',
              textOutline: 'none',
            },
          },
        },
      },
      series,
    };
  }

  private drawChart(): void {
    if (!this.chartContainer) return;

    this.chart?.destroy();
    this.chart = Highcharts.chart(
      this.chartContainer.nativeElement,
      this.chartOptions
    );
  }
}
